// Run-Time Flags — the per-account runtime switches, one row per ACCOUNT (a
// company on a channel), grouped by company. Admin-only (App.jsx gates the tab
// on admin; the API refuses anything else).
//
// Four switches, four different consequences, all stored in company_settings:
//
//   Ingestion         whether we CAPTURE this account's messages at all. Also
//                     gated by env/infra (the poller, channel reads) — the DB
//                     switch subtracts only, so turning it on cannot start a
//                     poller infra has switched off. When that's the case the
//                     row says which flag is holding it closed instead of
//                     pretending the toggle did nothing.
//   AI generation     the poller drafts suggestions. Costs model spend; sends
//                     nothing.
//   Visible to CSRs   the account's conversations appear in the queue/triage.
//                     Admins can always see a dark account via the Company
//                     filter.
//   Outbound          m365_email accounts only — 'off'|'test'|'live', whether
//                     and where a reply from this account can actually be
//                     sent. 'live' is the mode that reaches a real customer.
//                     Also gated by environment config (M365_WRITE_ENABLED,
//                     the outbound door, the test-recipient var) — same
//                     intent-vs-reality split as Ingestion above.
//
// EVERY change — on or off, either direction, or a new outbound mode — is
// confirmed by typing the account code. Not a yes/no dialog: these are
// production switches on live message capture and on what every CSR sees (and,
// for Outbound, on whether CXHub can email a real customer), and the account
// code is per-row, so typing it proves the admin is acting on the row they
// think they are. A mis-aimed click on the wrong row cannot get past it.
//
// Spam rules and Flows are edited on their own pages; the columns here only
// report whether they can be written for the account.
//
// Accounts the registry can't resolve to a company — M365's TEST_EMAIL, which
// is a mailbox key rather than an account code — are grouped under "Unknown
// company" rather than dropped, so a mailbox that is ingesting can never be
// invisible here.
import { useEffect, useState, useCallback, Fragment, useMemo } from 'react'
import {
  Box, Stack, Typography, Chip, Button, Paper, CircularProgress, Alert, Tooltip, Switch, TextField,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogContentText, DialogActions,
  Select, MenuItem,
} from '@mui/material'
import { fetchCompanySettings, updateCompanySettings } from '../../api.js'
import { COMPANY_TAG_STYLE } from '../../constants/companies.js'
import { CHANNEL_LABELS } from '../../constants/channels.js'

const UNKNOWN = '__unknown__'

// What each switch does, in the words the confirm dialog uses. Keyed by the
// PATCH field name so the dialog, the table header and the request can never
// drift apart.
const SWITCHES = {
  ingestionEnabled: {
    label: 'Ingestion',
    on: 'Messages from this account will be captured again on the next poll.',
    off: 'This account stops capturing messages. Anything a customer sends while it is off is NOT backfilled later — the poll is latest-page-only.',
  },
  generationEnabled: {
    label: 'AI generation',
    on: 'The poller will draft AI suggestions for this account\'s new messages. Costs model spend. Sends nothing.',
    off: 'No new AI drafts for this account. Existing suggestions are untouched, and per-conversation Generate still works.',
  },
  surfaced: {
    label: 'Visible to CSRs',
    on: 'This account\'s conversations appear in the queue and triage for EVERY CSR, not just admins. If its AI drafts have not been reviewed yet, CSRs will be the first to see them.',
    off: 'This account goes dark: its conversations leave the CSR queue and triage. Admins can still see them via the Company filter.',
  },
  // Mode-valued, not on/off — 'off'|'test'|'live', m365_email accounts only.
  // Keyed by mode rather than on/off so the dialog can show the meaning of
  // whichever mode the admin just picked in the Select.
  outboundMode: {
    label: 'Outbound',
    modes: {
      off: 'Nothing sends for this account, whatever else is configured.',
      test: 'Every outbound email is hard-clamped to the one operator-configured test recipient (the M365_SEND_TEST_RECIPIENT environment variable). No customer address is reachable.',
      live: 'Outbound emails may go to any single address. This is the mode that emails real customers — the first customer-facing email CXHub ever sends goes out under it.',
    },
  },
}

function formatDate(iso) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('en-US', {
    timeZone: 'America/New_York', month: 'short', day: 'numeric',
    year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  }) + ' ET'
}

// Typed confirmation. The account code must match EXACTLY — no trimming of
// interior text, no case-insensitivity — because the whole point is that the
// admin reproduces the identifier of the row they are changing.
//
// `pending.next` is a boolean for the three on/off switches, and a mode
// string ('off'|'test'|'live') for outboundMode — isMode below is the only
// branch point; the boolean switches' rendering is unchanged.
function ConfirmToggleDialog({ pending, saving, error, onConfirm, onCancel }) {
  const [typed, setTyped] = useState('')
  const open = Boolean(pending)

  // Clear between openings so a previous confirmation can't be reused for the
  // next row by leaving the field populated.
  useEffect(() => { if (!open) setTyped('') }, [open])

  if (!open) return null
  const { row, field, next } = pending
  const meta = SWITCHES[field]
  const matches = typed === row.company
  const isMode = field === 'outboundMode'

  return (
    <Dialog open onClose={() => !saving && onCancel()} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isMode
          ? `Set ${meta.label} to ${next.toUpperCase()} for ${row.displayName ?? row.company}?`
          : `Turn ${meta.label} ${next ? 'ON' : 'OFF'} for ${row.displayName ?? row.company}?`}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <DialogContentText sx={{ mb: 2 }}>{isMode ? meta.modes[next] : (next ? meta.on : meta.off)}</DialogContentText>
        {isMode && next === 'live' && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This lets CXHub email real customers from <b>{row.outboundFrom}</b>.
          </Alert>
        )}
        {field === 'ingestionEnabled' && next && row.ingestionBlockedBy && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will save, but ingestion still will not run: {row.ingestionBlockedBy}. That is
            environment config and needs a deploy.
          </Alert>
        )}
        {isMode && next !== 'off' && row.outboundBlockedBy && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            This will save, but outbound still will not send: {row.outboundBlockedBy}. That is
            environment config and needs a deploy.
          </Alert>
        )}
        <DialogContentText sx={{ mb: 1 }}>
          Type <b>{row.company}</b> to confirm.
        </DialogContentText>
        <TextField
          autoFocus
          fullWidth
          size="small"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && matches && !saving) onConfirm() }}
          placeholder={row.company}
          inputProps={{ style: { fontFamily: 'monospace' }, 'aria-label': 'account code confirmation' }}
          disabled={saving}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          color={isMode ? (next === 'live' ? 'warning' : 'primary') : (next ? 'primary' : 'warning')}
          onClick={onConfirm}
          disabled={!matches || saving}
          startIcon={saving ? <CircularProgress size={14} color="inherit" /> : null}
        >
          {saving ? 'Saving…' : (isMode ? `Set to ${next}` : `Turn ${next ? 'on' : 'off'}`)}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

function ToggleCell({ row, field, busy, onRequest, children }) {
  return (
    <TableCell>
      <Stack direction="row" spacing={0.5} alignItems="center">
        <Switch
          size="small"
          checked={row[field]}
          disabled={busy}
          onChange={(e) => onRequest(row, field, e.target.checked)}
          inputProps={{ 'aria-label': `${SWITCHES[field].label} for ${row.company}` }}
        />
        {children}
      </Stack>
    </TableCell>
  )
}

// Outbound column. Only m365_email accounts with a resolved send identity get
// the live Select — everything else (eBay rows, or an m365 row that has no
// outboundFrom, e.g. a permanently no-reply mailbox) gets an em-dash with a
// tooltip explaining why there is nothing to switch.
function OutboundCell({ row, busy, onRequest }) {
  if (row.channel === 'm365_email' && row.outboundFrom) {
    return (
      <TableCell>
        <Stack spacing={0.25}>
          <Stack direction="row" spacing={0.5} alignItems="center">
            <Select
              size="small"
              value={row.outboundMode}
              disabled={busy}
              onChange={(e) => onRequest(row, 'outboundMode', e.target.value)}
              inputProps={{ 'aria-label': `Outbound for ${row.company}` }}
            >
              <MenuItem value="off">Off</MenuItem>
              <MenuItem value="test">Test recipient only</MenuItem>
              <MenuItem value="live">Live</MenuItem>
            </Select>
            {/* Same intent-vs-reality split as Ingestion's own blocked chip. */}
            {row.outboundMode !== 'off' && row.outboundBlockedBy && (
              <Tooltip title={`${row.outboundBlockedBy} — environment config, needs a deploy`}>
                <Chip size="small" variant="outlined" color="warning" label="blocked" />
              </Tooltip>
            )}
          </Stack>
          <Typography variant="caption" color="text.secondary" fontFamily="monospace">
            from {row.outboundFrom}
          </Typography>
        </Stack>
      </TableCell>
    )
  }
  return (
    <TableCell>
      <Tooltip title={row.outboundBlockedBy ?? ''}>
        <span>—</span>
      </Tooltip>
    </TableCell>
  )
}

function StateChip({ on, onLabel = 'On', offLabel = 'Off', title }) {
  const chip = (
    <Chip
      size="small"
      variant={on ? 'filled' : 'outlined'}
      color={on ? 'success' : 'default'}
      label={on ? onLabel : offLabel}
    />
  )
  return title ? <Tooltip title={title}><span>{chip}</span></Tooltip> : chip
}

function AccountRow({ row, busy, onRequest }) {
  return (
    <TableRow hover>
      <TableCell>{CHANNEL_LABELS[row.channel] ?? row.channel ?? '—'}</TableCell>
      <TableCell>
        <Typography variant="body2" fontFamily="monospace">{row.company}</Typography>
      </TableCell>
      <ToggleCell row={row} field="ingestionEnabled" busy={busy} onRequest={onRequest}>
        {/* The switch shows INTENT; this shows REALITY. They differ whenever the
            env chain is closed, and silently hiding that is how an admin
            concludes the toggle is broken. */}
        {row.ingestionEnabled && row.ingestionBlockedBy && (
          <Tooltip title={`${row.ingestionBlockedBy} — environment config, needs a deploy`}>
            <Chip size="small" variant="outlined" color="warning" label="blocked" />
          </Tooltip>
        )}
      </ToggleCell>
      <ToggleCell row={row} field="generationEnabled" busy={busy} onRequest={onRequest} />
      <ToggleCell row={row} field="surfaced" busy={busy} onRequest={onRequest} />
      <OutboundCell row={row} busy={busy} onRequest={onRequest} />
      <TableCell><StateChip on={row.spamRulesWritable} onLabel="Writable" offLabel="No" /></TableCell>
      <TableCell>
        <StateChip
          on={row.flowsWritable}
          onLabel="Writable"
          offLabel="No"
          title={row.flowsWritable
            ? `${row.flowCount} flows in the catalog, ${row.flowsAutomated} on Automate`
            : 'No flow catalog rows for this account'}
        />
      </TableCell>
      <TableCell>
        <Typography variant="caption" color="text.secondary">
          {row.updatedBy ? `${row.updatedBy} — ` : ''}{formatDate(row.updatedAt)}
        </Typography>
      </TableCell>
    </TableRow>
  )
}

export default function RuntimeFlagsPage() {
  const [rows, setRows] = useState([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pending, setPending] = useState(null)   // { row, field, next }
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState(null)

  const load = useCallback(() => {
    setLoading(true); setError(null)
    fetchCompanySettings()
      .then(setRows)
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false))
  }, [])

  useEffect(() => { load() }, [load])

  const requestToggle = useCallback((row, field, next) => {
    setSaveError(null)
    setPending({ row, field, next })
  }, [])

  const confirm = useCallback(async () => {
    if (!pending) return
    const { row, field, next } = pending
    setSaving(true); setSaveError(null)
    try {
      const updated = await updateCompanySettings(row.company, { [field]: next })
      // The PATCH response is the settings row only (no brand/channel/flow
      // fields), so merge onto the existing entry rather than replacing it.
      // ingesting/sending are each derived from BOTH halves of their gate, and
      // the response carries neither — recompute them here from what we
      // already know rather than letting a stale value sit until the next
      // refresh.
      setRows((prev) => prev.map((r) => {
        if (r.company !== row.company) return r
        const merged = { ...r, ...updated }
        return {
          ...merged,
          ingesting: merged.ingestionEnabled && r.ingestionBlockedBy === null,
          sending: merged.outboundMode !== 'off' && r.outboundBlockedBy === null,
        }
      }))
      setPending(null)
      // outboundBlockedBy can differ between test and live mode (test mode
      // additionally needs the test-recipient env var) — the row above kept
      // the PRE-save value, so refetch to get the accurate one rather than
      // waiting on the next manual Refresh.
      if (field === 'outboundMode') load()
    } catch (e) {
      setSaveError(e.message)
    } finally {
      setSaving(false)
    }
  }, [pending, load])

  // Group by company, registered companies alphabetically and the unresolvable
  // ones last — an account with no company is an anomaly to notice, not the
  // first thing the page shows.
  const groups = useMemo(() => {
    const byCompany = new Map()
    for (const r of rows) {
      const key = r.companyCode ?? UNKNOWN
      if (!byCompany.has(key)) {
        byCompany.set(key, { key, companyCode: r.companyCode, displayName: r.displayName, rows: [] })
      }
      byCompany.get(key).rows.push(r)
    }
    return [...byCompany.values()].sort((a, b) => {
      if (a.key === UNKNOWN) return 1
      if (b.key === UNKNOWN) return -1
      return (a.displayName ?? '').localeCompare(b.displayName ?? '')
    })
  }, [rows])

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>Run-Time Flags</Typography>
        <Box sx={{ flexGrow: 1 }} />
        <Button size="small" onClick={load} disabled={loading}>Refresh</Button>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Every account (a company on a channel) and what is switched on for it. Each change is
        confirmed by typing the account code. <b>Ingestion</b> is also gated by environment config —
        turning it off here always stops capture, but turning it on cannot start a poller that
        infra has switched off, and the row says so when that is the case. <b>Outbound</b> is a
        per-account mode (m365 email accounts only) for whether and where this account can send —
        <b> live</b> is the mode that lets CXHub email a real customer. <b>Spam rules</b> and{' '}
        <b>Flows</b> are edited on their own pages; this only reports whether they can be written
        for the account.
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {saveError && !pending && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setSaveError(null)}>{saveError}</Alert>}

      {loading ? (
        <Box sx={{ py: 4, textAlign: 'center' }}><CircularProgress size={28} /></Box>
      ) : rows.length === 0 ? (
        <Typography color="text.secondary">No accounts registered.</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Channel</TableCell>
                <TableCell>Account</TableCell>
                <TableCell>Ingestion</TableCell>
                <TableCell>AI generation</TableCell>
                <TableCell>Visible to CSRs</TableCell>
                <TableCell>Outbound</TableCell>
                <TableCell>Spam rules</TableCell>
                <TableCell>Flows</TableCell>
                <TableCell>Settings last changed</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {groups.map((g) => (
                <Fragment key={g.key}>
                  <TableRow>
                    <TableCell colSpan={9} sx={{ bgcolor: 'action.hover' }}>
                      <Stack direction="row" spacing={1.5} alignItems="center">
                        {g.companyCode ? (
                          <Chip
                            size="small"
                            variant="filled"
                            label={g.companyCode}
                            sx={{ ...(COMPANY_TAG_STYLE[g.companyCode] ?? {}), fontFamily: 'monospace', fontWeight: 700 }}
                          />
                        ) : (
                          <Chip size="small" variant="outlined" color="warning" label="?" sx={{ fontFamily: 'monospace', fontWeight: 700 }} />
                        )}
                        <Typography fontWeight={700}>{g.displayName ?? 'Unknown company'}</Typography>
                        {!g.companyCode && (
                          <Typography variant="caption" color="text.secondary">
                            account code not in the company registry
                          </Typography>
                        )}
                      </Stack>
                    </TableCell>
                  </TableRow>
                  {g.rows.map((r) => (
                    <AccountRow
                      key={`${r.company}:${r.channel}`}
                      row={r}
                      busy={saving && pending?.row.company === r.company}
                      onRequest={requestToggle}
                    />
                  ))}
                </Fragment>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      <ConfirmToggleDialog
        pending={pending}
        saving={saving}
        error={saveError}
        onConfirm={confirm}
        onCancel={() => { if (!saving) { setPending(null); setSaveError(null) } }}
      />
    </Box>
  )
}
